import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { newOptionKey, newQuestionId } from "@/lib/surveys/ids";
import type { Lang, Localized, QuestionType, SurveyQuestion } from "@/lib/surveys/types";
import { Field, inputCls, Toggle } from "./form";

export const TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: "Single choice",
  multi_choice: "Multiple choice",
  rating: "Rating",
  nps: "NPS 0–10",
  text: "Text",
};

const empty = (): Localized => ({ en: "", hi: "" });

/** A new question of [type] with sensible defaults. */
export function newQuestion(type: QuestionType, taken: Set<string>): SurveyQuestion {
  const q: SurveyQuestion = { id: newQuestionId(taken), type, text: empty(), required: true };
  return withTypeDefaults(q, type);
}

function withTypeDefaults(q: SurveyQuestion, type: QuestionType): SurveyQuestion {
  const base: SurveyQuestion = { id: q.id, type, text: q.text, required: q.required };
  if (type === "single_choice" || type === "multi_choice") {
    const keys = new Set<string>();
    const opt = () => {
      const key = newOptionKey(keys);
      keys.add(key);
      return { key, text: empty() };
    };
    return { ...base, options: q.options?.length ? q.options : [opt(), opt()], allowOther: q.allowOther ?? false };
  }
  if (type === "rating") return { ...base, rating: { scale: 5, style: "faces" } };
  if (type === "nps") {
    return { ...base, nps: { lowLabel: { en: "Not likely", hi: "बिल्कुल नहीं" }, highLabel: { en: "Very likely", hi: "बहुत ज़्यादा" } } };
  }
  return base;
}

interface Props {
  questions: SurveyQuestion[];
  lang: Lang;
  locked: boolean;
  readOnly: boolean;
  onChange: (questions: SurveyQuestion[]) => void;
}

export function QuestionEditor({ questions, lang, locked, readOnly, onChange }: Props) {
  const structural = locked || readOnly;
  const set = (i: number, q: SurveyQuestion) => onChange(questions.map((x, j) => (j === i ? q : x)));
  const move = (i: number, d: -1 | 1) => {
    const next = [...questions];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  const add = (type: QuestionType) =>
    onChange([...questions, newQuestion(type, new Set(questions.map((q) => q.id)))]);

  return (
    <div className="space-y-3">
      {locked && (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-2.5 text-[12.5px] text-amber-300">
          This survey is live, so questions are locked and existing answers stay valid. You can still fix wording and translations.
        </div>
      )}

      {questions.length === 0 && (
        <p className="text-[13px] text-zinc-500">No questions yet. Add one below.</p>
      )}

      {questions.map((q, i) => (
        <div key={q.id} data-testid="question-card" className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-white/[0.06] grid place-items-center text-[12px] font-bold text-zinc-400">{i + 1}</span>
            <select
              aria-label={`Question ${i + 1} type`}
              value={q.type}
              disabled={structural}
              onChange={(e) => set(i, withTypeDefaults(q, e.target.value as QuestionType))}
              className={`${inputCls} w-auto py-1.5`}
            >
              {Object.entries(TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v} className="bg-[#0b0b0d]">{l}</option>
              ))}
            </select>
            <Toggle label="Required" checked={q.required} disabled={structural} onChange={(v) => set(i, { ...q, required: v })} />
            <div className="flex-1" />
            <IconBtn label="Move up" disabled={structural || i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></IconBtn>
            <IconBtn label="Move down" disabled={structural || i === questions.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></IconBtn>
            <IconBtn label="Delete question" disabled={structural} onClick={() => onChange(questions.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
          </div>

          <Field label={`Question (${lang.toUpperCase()})`}>
            <input
              className={inputCls}
              value={q.text[lang]}
              disabled={readOnly}
              placeholder={lang === "hi" ? q.text.en || "Hindi text" : "e.g. How do you feel about Fincurry?"}
              onChange={(e) => set(i, { ...q, text: { ...q.text, [lang]: e.target.value } })}
            />
          </Field>

          {(q.type === "single_choice" || q.type === "multi_choice") && (
            <div className="space-y-2">
              {q.options?.map((o, j) => (
                <div key={o.key} className="flex items-center gap-2">
                  <input
                    aria-label={`Option ${j + 1}`}
                    className={inputCls}
                    value={o.text[lang]}
                    disabled={readOnly}
                    placeholder={lang === "hi" ? o.text.en || "Hindi text" : `Option ${j + 1}`}
                    onChange={(e) =>
                      set(i, { ...q, options: q.options!.map((x, k) => (k === j ? { ...x, text: { ...x.text, [lang]: e.target.value } } : x)) })
                    }
                  />
                  <IconBtn label={`Remove option ${j + 1}`} disabled={structural || (q.options?.length ?? 0) <= 2} onClick={() => set(i, { ...q, options: q.options!.filter((_, k) => k !== j) })}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </IconBtn>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  disabled={structural}
                  onClick={() => set(i, { ...q, options: [...(q.options ?? []), { key: newOptionKey(new Set(q.options?.map((o) => o.key))), text: empty() }] })}
                  className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-emerald-400 disabled:opacity-40"
                >
                  <Plus className="w-3.5 h-3.5" /> Add option
                </button>
                <Toggle label='"Other" with text' checked={q.allowOther ?? false} disabled={structural} onChange={(v) => set(i, { ...q, allowOther: v })} />
                {q.type === "multi_choice" && (
                  <label className="flex items-center gap-2 text-[12.5px] text-zinc-400">
                    Pick up to
                    <input
                      type="number"
                      min={1}
                      aria-label="Pick up to"
                      className={`${inputCls} w-20 py-1.5`}
                      disabled={structural}
                      value={q.maxSelect ?? ""}
                      placeholder="all"
                      onChange={(e) => set(i, { ...q, maxSelect: e.target.value ? Number(e.target.value) : undefined })}
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {q.type === "rating" && q.rating && (
            <div className="flex flex-wrap gap-4">
              <Field label="Scale">
                <select
                  className={`${inputCls} w-auto py-1.5`}
                  disabled={structural}
                  value={q.rating.scale}
                  onChange={(e) => {
                    const scale = Number(e.target.value);
                    set(i, { ...q, rating: { scale, style: scale === 5 ? q.rating!.style : "stars" } });
                  }}
                >
                  {Array.from({ length: 9 }, (_, k) => k + 2).map((n) => (
                    <option key={n} value={n} className="bg-[#0b0b0d]">{n} points</option>
                  ))}
                </select>
              </Field>
              <Field label="Style">
                <select
                  className={`${inputCls} w-auto py-1.5`}
                  disabled={readOnly}
                  value={q.rating.style}
                  onChange={(e) => set(i, { ...q, rating: { ...q.rating!, style: e.target.value as "faces" | "stars" } })}
                >
                  <option value="stars" className="bg-[#0b0b0d]">Stars</option>
                  <option value="faces" disabled={q.rating.scale !== 5} className="bg-[#0b0b0d]">Faces (5 only)</option>
                </select>
              </Field>
            </div>
          )}

          {q.type === "nps" && q.nps && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={`Low label (${lang.toUpperCase()})`}>
                <input className={inputCls} disabled={readOnly} value={q.nps.lowLabel[lang]} onChange={(e) => set(i, { ...q, nps: { ...q.nps!, lowLabel: { ...q.nps!.lowLabel, [lang]: e.target.value } } })} />
              </Field>
              <Field label={`High label (${lang.toUpperCase()})`}>
                <input className={inputCls} disabled={readOnly} value={q.nps.highLabel[lang]} onChange={(e) => set(i, { ...q, nps: { ...q.nps!, highLabel: { ...q.nps!.highLabel, [lang]: e.target.value } } })} />
              </Field>
            </div>
          )}
        </div>
      ))}

      {!structural && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-white/[0.1] p-3">
          <span className="text-[12.5px] font-semibold text-zinc-400 mr-1">+ Add question:</span>
          {(Object.keys(TYPE_LABELS) as QuestionType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => add(t)}
              className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12.5px] text-zinc-200 hover:border-emerald-500/30"
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function IconBtn({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="w-7 h-7 rounded-lg border border-white/[0.06] grid place-items-center text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:text-zinc-400"
    >
      {children}
    </button>
  );
}
